import type {
  AutomationRule,
  CalendarFieldMapping,
  GanttFieldMapping,
  WritebackConfig,
} from '@/types/pm-types';
import { isRecord } from '@/types/componentDataBinding';
import type { CalendarViewMode } from './calendarDateUtils';
import type { TimeScale } from './ganttTimeUtils';

export function readCalendarViewMode(value: unknown): CalendarViewMode {
  return value === 'month' || value === 'week' || value === 'day' || value === 'agenda'
    ? value
    : 'month';
}

export function readGanttTimeScale(value: unknown): TimeScale {
  return value === 'day' || value === 'week' || value === 'month' || value === 'quarter'
    ? value
    : 'week';
}

export function readFiniteNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value) && value !== 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed !== 0) {
      return parsed;
    }
  }

  return fallback;
}

export function readNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export function readStringRecord(value: unknown): Record<string, string> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const record: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry !== 'string') {
      return undefined;
    }
    record[key] = entry;
  }

  return record;
}

export function readCalendarFieldMapping(value: unknown): CalendarFieldMapping {
  const fieldMapping: CalendarFieldMapping = {
    id: 'id',
    title: 'title',
    startDate: 'start',
    endDate: 'end',
  };

  if (!isRecord(value)) {
    return fieldMapping;
  }

  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') {
      fieldMapping[key] = entry;
    }
  }

  return fieldMapping;
}

export function readGanttFieldMapping(
  value: unknown,
  defaultFieldMapping: GanttFieldMapping,
): GanttFieldMapping {
  const fieldMapping: GanttFieldMapping = { ...defaultFieldMapping };

  if (!isRecord(value)) {
    return fieldMapping;
  }

  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') {
      fieldMapping[key] = entry;
    }
  }

  return fieldMapping;
}

export function readWritebackConfig(value: unknown): WritebackConfig | undefined {
  if (
    !isRecord(value) ||
    typeof value.enabled !== 'boolean' ||
    typeof value.groupId !== 'string' ||
    typeof value.schemaId !== 'string' ||
    typeof value.idField !== 'string'
  ) {
    return undefined;
  }

  return {
    enabled: value.enabled,
    groupId: value.groupId,
    schemaId: value.schemaId,
    idField: value.idField,
  };
}

function isAutomationRule(value: unknown): value is AutomationRule {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.enabled === 'boolean' &&
    isRecord(value.trigger) &&
    Array.isArray(value.conditions) &&
    Array.isArray(value.actions)
  );
}

export function readAutomationRules(value: unknown): AutomationRule[] | undefined {
  return Array.isArray(value) && value.every(isAutomationRule) ? value : undefined;
}
